import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

/** General site settings (Site Control → General): site name, logo, accent
    color. Publicly readable; empty values mean "use the built-in default",
    so every consumer must keep its current fallback. */
export function useSiteSettings() {
  return useQuery({
    queryKey: ["site_settings"],
    queryFn: async () => {
      const { data, error } = await (supabase as any).from("site_settings").select("key, value");
      if (error) throw error;
      return Object.fromEntries(
        (data ?? []).map((r: any) => [r.key, String(r.value ?? "")]),
      ) as Record<string, string>;
    },
    staleTime: 5 * 60_000,
    retry: false,
  });
}

const HEX_RE = /^#[0-9a-fA-F]{6}$/;

function hexToHsl(hex: string): string | null {
  if (!HEX_RE.test(hex)) return null;
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0, s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
    else if (max === g) h = ((b - r) / d + 2) / 6;
    else h = ((r - g) / d + 4) / 6;
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

/** Applies the configured accent color to the theme's --accent token (the
    shadcn accent used by themed components). No-op when unset or invalid;
    hardcoded brand hexes are untouched by design. */
export function useApplySiteAccent() {
  const { data: settings } = useSiteSettings();
  useEffect(() => {
    const hsl = settings?.accent_color ? hexToHsl(settings.accent_color) : null;
    const root = document.documentElement;
    if (hsl) {
      root.style.setProperty("--accent", hsl);
    } else {
      root.style.removeProperty("--accent");
    }
  }, [settings?.accent_color]);
}
